import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export interface CreateCouponPayload {
  code: string;
  description: string;
  discount_type: "PERCENTAGE" | "FIXED_AMOUNT";
  discount_on: "PRICE" | "CREDITS" | "BOTH";
  discount_value: number | null;
  credit_bonus: number | null;
  max_usage: number | null;
  max_usage_per_user: number | null;
  minimum_amount: number | null;
  maximum_amount: number | null;
  expiry: string | null;
  status: "ACTIVE" | "EXPIRED" | "DISABLED";
}

interface CreateCouponFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateCouponPayload) => void;
}

const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required("Code is required")
      .min(1, "Code is too short")
      .max(10, "Code is too long")
      .transform((v: string) => v.trim().toUpperCase()),
    description: Yup.string().required("Description is required"),
    discount_type: Yup.mixed<"PERCENTAGE" | "FIXED_AMOUNT">()
      .oneOf(["PERCENTAGE", "FIXED_AMOUNT"], "Invalid discount type")
      .required("Discount type is required"),
    discount_on: Yup.mixed<"PRICE" | "CREDITS" | "BOTH">()
      .oneOf(["PRICE", "CREDITS", "BOTH"], "Invalid discount on")
      .required("Discount on is required"),
    discount_value: Yup.number()
      .nullable()
      .when("discount_on", {
        is: (val: string) => val === "PRICE" || val === "BOTH",
        then: (schema) => schema.required("Discount value required").min(0, "Must be >= 0"),
        otherwise: (schema) => schema.nullable(),
      })
      .when("discount_type", {
        is: "PERCENTAGE",
        then: (schema) => schema.max(100, "Percentage cannot exceed 100"),
      }),
    credit_bonus: Yup.number()
      .nullable()
      .when("discount_on", {
        is: (val: string) => val === "CREDITS" || val === "BOTH",
        then: (schema) => schema.required("Credit bonus required").min(0, "Must be >= 0"),
        otherwise: (schema) => schema.nullable(),
      }),
    max_usage: Yup.number()
      .nullable()
      .min(1, "Must be at least 1"),
    max_usage_per_user: Yup.number()
      .nullable()
      .min(1, "Must be at least 1"),
    minimum_amount: Yup.number().nullable().min(0, "Must be >= 0"),
    maximum_amount: Yup.number()
      .nullable()
      .min(0, "Must be >= 0")
      .test("min-max", "Maximum must be >= minimum", function (value) {
        const { minimum_amount } = this.parent;
        if (minimum_amount != null && value != null) {
          return value >= minimum_amount;
        }
        return true;
      }),
    expiry: Yup.date()
      .nullable()
      .min(new Date(), "Expiry must be in the future")
      .typeError("Invalid date"),
    status: Yup.mixed<"ACTIVE" | "EXPIRED" | "DISABLED">()
      .oneOf(["ACTIVE", "EXPIRED", "DISABLED"], "Invalid status")
      .required("Status is required"),
  });

const CreateCoupon: React.FC<CreateCouponFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const initialState: any = {
    code: "",
    description: "",
    discount_type: "PERCENTAGE",
    discount_on: "PRICE",
    discount_value: null,
    credit_bonus: null,
    max_usage: null,
    max_usage_per_user: null,
    minimum_amount: null,
    maximum_amount: null,
    expiry: null,
    status: "ACTIVE",
  };

  const [values, setValues] = useState<any>(initialState);
  const [errors, setErrors] = useState<
    Partial<Record<keyof typeof initialState, string>>
  >({});
  const CreateCouponCodeResponse = useSelector(
    (state: any) => state.CreateCouponResponse
  );

  useEffect(() => {
    if (CreateCouponCodeResponse?.status && CreateCouponCodeResponse?.data) {
      toast.success(CreateCouponCodeResponse?.data?.data?.message);
      setValues(initialState);
      onClose();
    }
  }, [CreateCouponCodeResponse]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | (SelectChangeEvent<string> & { name?: string })
  ) => {
    const { name, value } = e.target;
    if (!name) return;

    setValues((prev: any) => ({
      ...prev,
      [name]:
        "type" in e.target && e.target.type === "number"
          ? value === ""
            ? ""
            : Number(value)
          : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const valid = await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});
      // prepare payload
      const payload: CreateCouponPayload = {
        code: valid.code,
        description: valid.description,
        discount_type: valid.discount_type,
        discount_on: valid.discount_on,
        discount_value: valid.discount_value ?? null,
        credit_bonus: valid.credit_bonus ?? null,
        max_usage: valid.max_usage ?? null,
        max_usage_per_user: valid.max_usage_per_user ?? null,
        minimum_amount: valid.minimum_amount ?? null,
        maximum_amount: valid.maximum_amount ?? null,
        expiry: valid.expiry ? new Date(valid.expiry).toISOString() : null,
        status: valid.status,
      };
      onSubmit(payload);
      setValues(initialState);
      onClose();
    } catch (err: any) {
      if (err.inner) {
        const formErrors: any = {};
        err.inner.forEach((ve: any) => {
          formErrors[ve.path] = ve.message;
        });
        setErrors(formErrors);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Coupon</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="code"
            label="Code"
            value={values.code}
            onChange={handleChange}
            error={Boolean(errors.code)}
            helperText={errors.code}
            fullWidth
          />
          <TextField
            name="description"
            label="Description"
            value={values.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            rows={3}
            fullWidth
          />
          <FormControl fullWidth error={Boolean(errors.discount_type)}>
            <InputLabel id="discount-type-label">Discount Type</InputLabel>
            <Select
              labelId="discount-type-label"
              name="discount_type"
              label="Discount Type"
              value={values.discount_type}
              onChange={handleChange}
            >
              <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              <MenuItem value="FIXED_AMOUNT">Fixed Amount</MenuItem>
            </Select>
            {errors.discount_type && (
              <Box sx={{ color: "error.main" }}>{errors.discount_type}</Box>
            )}
          </FormControl>
          <FormControl fullWidth error={Boolean(errors.discount_on)}>
            <InputLabel id="discount-on-label">Discount On</InputLabel>
            <Select
              labelId="discount-on-label"
              name="discount_on"
              label="Discount On"
              value={values.discount_on}
              onChange={handleChange}
            >
              <MenuItem value="PRICE">Price</MenuItem>
              <MenuItem value="CREDITS">Credits</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </Select>
            {errors.discount_on && (
              <Box sx={{ color: "error.main" }}>{errors.discount_on}</Box>
            )}
          </FormControl>
          <TextField
            name="discount_value"
            label="Discount Value"
            type="number"
            inputProps={{ min: 0 }}
            value={values.discount_value ?? ""}
            onChange={handleChange}
            error={Boolean(errors.discount_value)}
            helperText={errors.discount_value}
            fullWidth
          />
          <TextField
            name="credit_bonus"
            label="Credit Bonus"
            type="number"
            inputProps={{ min: 0 }}
            value={values.credit_bonus ?? ""}
            onChange={handleChange}
            error={Boolean(errors.credit_bonus)}
            helperText={errors.credit_bonus}
            fullWidth
          />
          <TextField
            name="max_usage"
            label="Max Usage"
            type="number"
            inputProps={{ min: 1 }}
            value={values.max_usage}
            onChange={handleChange}
            error={Boolean(errors.max_usage)}
            helperText={errors.max_usage}
            fullWidth
          />
          <TextField
            name="max_usage_per_user"
            label="Max Usage Per User"
            type="number"
            inputProps={{ min: 1 }}
            value={values.max_usage_per_user}
            onChange={handleChange}
            error={Boolean(errors.max_usage_per_user)}
            helperText={errors.max_usage_per_user}
            fullWidth
          />
          <TextField
            name="minimum_amount"
            label="Minimum Amount"
            type="number"
            inputProps={{ min: 0 }}
            value={values.minimum_amount ?? ""}
            onChange={handleChange}
            error={Boolean(errors.minimum_amount)}
            helperText={errors.minimum_amount}
            fullWidth
          />
          <TextField
            name="maximum_amount"
            label="Maximum Amount"
            type="number"
            inputProps={{ min: 0 }}
            value={values.maximum_amount ?? ""}
            onChange={handleChange}
            error={Boolean(errors.maximum_amount)}
            helperText={errors.maximum_amount}
            fullWidth
          />
          <TextField
            name="expiry"
            label="Expiry Date"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={values.expiry ?? ""}
            onChange={handleChange}
            error={Boolean(errors.expiry)}
            helperText={errors.expiry}
            fullWidth
          />
          <FormControl fullWidth error={Boolean(errors.status)}>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              label="Status"
              value={values.status}
              onChange={handleChange}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
              <MenuItem value="DISABLED">Disabled</MenuItem>
            </Select>
            {errors.status && (
              <Box sx={{ color: "error.main" }}>{errors.status}</Box>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Coupon
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCoupon;
